import { Component, OnInit, AfterViewInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { Form } from '../../../shared/models/form-builder/form.model';
import { FolderService } from '../../../shared/services/folder.service';
import { FormInstance } from '../../../shared/models/site-content/form-instance.model';
import { MostRecentFormSelectedService } from '../../../shared/services/most-recent-form-selected.service';
import { AlertDialogComponent, AlertDialogModel } from '../alert/alert-dialog.component';
import { ScriptableBaseComponent } from '../../../shared/components/scriptable-base/scriptable-base.component';
import { Scriptable } from '../../../shared/models/component-scripting/scriptable-function.annotation';
import { ComponentMethodsService, EnumMetadata } from '../../../shared/models/component-scripting/component-methods.service';
import { ComponentMethodsMetadata, ComponentMethodMetadata } from '../../../shared/models/component-scripting/component-methods-metadata.model';
import { ComponentHierarchyService } from '../../../shared/services/component-hierarchy.service';
import { INameToPrettyNameMap } from '../../models/component-scripting/additional-element-info.model';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CurrentSiteService } from '../../../shared/services/current-site.service';

const FORM_CONTROL: string = 'form';
const DESTINATION_FOLDER_CONTROL: string = 'destination-folder-control';

const FormNamesEnum: string = 'FormNamesEnum';

export class NewFormInstanceDialogManager {
    public constructor(public currentFolder: Folder, public componentHierarchyService: ComponentHierarchyService) {
    }

    public openDialogUsing(dialog: MatDialog): MatDialogRef<NewFormInstanceDialog, any> {
        let initData = {
            width: '40%',
            hasBackdrop: this.componentHierarchyService.UITestCenterDialog == null,
            data: {
                currentFolder: this.currentFolder,
                formInstances: this.currentFolder.formInstances
            }
        };

        const dialogRef = dialog.open(NewFormInstanceDialog, initData);
        return dialogRef;
    }
}

@Component({
    selector: 'app-new-form-instance',
    templateUrl: './new-form-instance.dialog.html',
    styleUrls: ['./new-form-instance.dialog.scss'],
    standalone: false
})
export class NewFormInstanceDialog extends ScriptableBaseComponent implements OnInit, AfterViewInit {
    // Instance data.
    public currentFolder: Folder = null; // Note:  making this public to reduce the number of framework callbacks into the Typescript code.
    public siteForms: Form[] = []; // Note:  making this public to reduce the number of framework callbacks into the Typescript code.
    public selectedFormId: number = 0; // Note:  making this public to reduce the number of framework callbacks into the Typescript code.
    public formInstanceName: string = null; // Note:  making this public to reduce the number of framework callbacks into the Typescript code.
    public addLocation: string = 'here'; // Note:  making this public to reduce the number of framework callbacks into the Typescript code.
    private existingFormInstances: FormInstance[];

    private openedFromHomePage: boolean = false; // introduced for VNEXT-902
    private writeableFolders: Folder[]; // introduced for VNEXT-902
    public destinationFolderId: number; // introduced for VNEXT-902 (following above examples by making it public)

    public destFolderFormControl = new UntypedFormControl('');
    public filteredWriteableFolders: Observable<Folder[]>;

    @ViewChild('formName') formNameInput: ElementRef;

    // Constructor.
    public constructor(public dialogRef: MatDialogRef<NewFormInstanceDialog>,
        @Inject(MAT_DIALOG_DATA) public hshInitData: any,
        public dialog: MatDialog,
        //private route: ActivatedRoute,
        //private dataCollectionService: DataCollectionService,
        private folderService: FolderService,
        private mostRecentFormSelectedService: MostRecentFormSelectedService,
        private currentSiteService: CurrentSiteService,
        elForBaseClass: ElementRef) {
        super(elForBaseClass);
        //this.element('#matDialogContent').hasMatSelect().hasInput();
        let namesMap: INameToPrettyNameMap =
        {
            'formName': 'Form Name'
        };
        this.element('#matDialogContent').hasInput().withNameToPrettyNameMap(namesMap);
        this.element('#matDialogActions').hasButtons();

        this.currentFolder = hshInitData['currentFolder'];
        this.existingFormInstances = hshInitData['formInstances'];
        this.openedFromHomePage = hshInitData['openedFromHomePage'];
        this.writeableFolders = hshInitData['writeableFolders'];
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Note:  cannot call super.ngOnInit() until all of the form templates have been loaded.
        //super.ngOnInit();

        // Load the list of available forms for this Folder
        if (!this.openedFromHomePage)
            this.loadAvailableForms();

        if (this.openedFromHomePage) {
            this.filteredWriteableFolders = this.destFolderFormControl.valueChanges.pipe(
                startWith(''),
                map(v => this.filterWriteableFolders(v || '')))
        }

    }

    private filterWriteableFolders(filterValue: any): Folder[]{
        if (typeof filterValue === 'string' ) {
            filterValue = filterValue.toLowerCase();
        }
        else{
            filterValue = filterValue.name.toLowerCase();
        }

        let filtered = this.writeableFolders.filter(x => x.name.toLowerCase().includes(filterValue));
        return filtered;
    }

    public displayFolderPath(folder: Folder): string {
        return (folder != null ? folder.name : '');
    }

    public ngAfterViewInit(): void {
        return;
    }

    // Accessor methods.
    public get Forms(): Form[] {
        return this.siteForms;
    }

    public get SelectedFormId(): number {
        return (this.selectedFormId);
    }

    public set SelectedFormId(selectedFormIdParam: number) {
        this.selectedFormId = selectedFormIdParam;
    }

    public get FormInstanceName(): string {
        return (this.formInstanceName);
    }

    public get AddLocation(): string {
        return (this.addLocation);
    }

    public get ChildFolderCount(): number {
        return this.currentFolder?.childFolders?.length;
    }

    public get OpenedFromHomePage(): boolean {
        return this.openedFromHomePage;
    }

    public get WriteableFolders(): Folder[] {
        return this.writeableFolders;
    }

    public get CurrentFolderChildFolderCount(): number {
        return (this.currentFolder.childFolders.length);
    }

    public set AddLocation(addLocationParam: string) {
        this.addLocation = addLocationParam;
    }

    public set FormInstanceName(formInstanceNameParam: string) {
        this.formInstanceName = formInstanceNameParam;
    }

    // ngModelChange handler.
    public handleNgModelChange(controlName: string): void {
        if ((controlName == FORM_CONTROL) && (this.selectedFormId > 0)) {
            let selectedForm: Form = this.siteForms.find(f => f.id == this.selectedFormId);

            let suggestedName = this.getSuggestedName(selectedForm);

            if (selectedForm != null) {
                this.formInstanceName = suggestedName;
            }
        } else if ((controlName == DESTINATION_FOLDER_CONTROL)) {
            // NOOP for now
        }

        return;
    }

    // Handle control events.
    public createClicked(): void { // "New Form" dialog from structure page, Create button clicked
        if (this.openedFromHomePage) {
            let destinationFolder: Folder = this.destFolderFormControl.value;

            this.dialogRef.close({
                destinationFolderId: destinationFolder.id,
                formInstanceName: this.formInstanceName
            });
        } else {
            // PJH - VNEXT-2260 - updated criteria for what is considered a dupe
            // A name is dupe if a Form with same name AND the same FormTemplate already exists
            const isDupe = this.existingFormInstances.filter(x => { return x.name == this.formInstanceName && x.formId == this.selectedFormId }).length > 0;
            if (isDupe) {
                let message: string = `A form named '${this.formInstanceName}' already exists in the current folder.`;
                let dialogueData: AlertDialogModel = new AlertDialogModel("Duplicate Form Name", message);
                this.dialog.open(AlertDialogComponent, {
                    maxWidth: "600px",
                    data: dialogueData
                });
                return;
            }

            let newFormInstance: FormInstance = new FormInstance();
            newFormInstance.dataCollectionId = this.currentSiteService.Site.id;
            if (this.addLocation == 'here') {
                newFormInstance.folderId = this.currentFolder.id;
                newFormInstance.formId = this.selectedFormId;
                newFormInstance.name = this.formInstanceName;

                // Set the most recently used form.
                let selectedForm: Form = this.siteForms.find(f => f.id == this.selectedFormId);
                if (selectedForm != null) {
                    this.mostRecentFormSelectedService.Form = selectedForm;
                }
                this.dialogRef.close(newFormInstance);
            }
            else {
                let formInstancesToAdd: FormInstance[] = [];
                let formInstanceName = this.formInstanceName;
                let formInstanceId = this.selectedFormId;

                this.currentFolder.childFolders.forEach(function (value) {
                    newFormInstance = new FormInstance();
                    newFormInstance.folderId = value.id;
                    newFormInstance.formId = formInstanceId;
                    newFormInstance.name = formInstanceName;
                    formInstancesToAdd.push(newFormInstance);
                });
                this.dialogRef.close(formInstancesToAdd);
            }
            return;
        }
    }

    // Define a method used for UI testing/scripting.
    @Scriptable('Select Form', ['formTemplateName'], [FormNamesEnum])
    public selectForm(formName: string): void {
        let form: Form = this.siteForms.find(f => f.name == formName);
        if (form != null) {
            this.selectedFormId = form.id;

            if ((this.formInstanceName == null) || (this.formInstanceName.trim() == ''))
                this.formInstanceName = formName;
        }
    }

    // Helper methods.
    private loadAvailableForms(): void {
        this.folderService.getAvailableForms(this.currentFolder.id).then(response => {
            // Save/process forms.
            this.siteForms = response;
            let defaultForm = this.siteForms.filter(x => { return x.isDefaultForFolder })[0];

            // Now call super.ngOnInit() because this component is ready for UI testing script commands.
            super.ngOnInit();

            if (defaultForm) {
                this.selectedFormId = defaultForm.id;
            }
            else if (this.mostRecentFormSelectedService.Form != null) {
                this.selectedFormId = this.mostRecentFormSelectedService.Form.id;
                this.handleNgModelChange(FORM_CONTROL);

                if ((this.formNameInput != null) && (this.selectedFormId > 0)) {
                    // Having to write the following code using a timeout
                    // is rather ugly, but without a timeout of some
                    // significant duration, in this case 100 milliseconds,
                    // the focus always ended up on the form name select.
                    setTimeout(() => {
                        const formNameInput: HTMLElement = this.formNameInput.nativeElement as HTMLElement;
                        formNameInput.focus();
                    }, 100);
                }
            } else if ((this.siteForms != null) && (this.siteForms.length == 1)) {
                // If there is only one form available, select it.
                this.selectedFormId = this.siteForms[0].id;
                this.handleNgModelChange(FORM_CONTROL);
            }

            // Configure the metadata for method selectForm().
            this.configureMethodSelectForm();
        });
    }

    private configureMethodSelectForm(): void {
        let methodsMetadata: ComponentMethodsMetadata = ComponentMethodsService.getComponentMethodsMetadataFor(this.constructor.name);
        if ((methodsMetadata != null) && (methodsMetadata.methodsMetadata != null)) {
            let methodMetadata: ComponentMethodMetadata = methodsMetadata.methodsMetadata.find(md => md.methodName == 'selectForm');
            if (methodMetadata != null) {
                let formNames: string[] = this.siteForms.map(f => f.name);
                let formNamesEnumDef: EnumMetadata = new EnumMetadata(FormNamesEnum, formNames);

                ComponentMethodsService.registerOrUpdateEnum(FormNamesEnum, formNamesEnumDef);
            }
        }
    }

    private getSuggestedName(selectedForm: Form = null) {
        let suggestedName: string = null;

        if (selectedForm.hasFormInstanceAutoNumberConfig()) {
            let numTemplateFormInstancesInFolder = (selectedForm.numTemplateFormInstancesInFolder != null) && (!isNaN(selectedForm.numTemplateFormInstancesInFolder)) ? selectedForm.numTemplateFormInstancesInFolder : 0;
            let folderName: string = this.currentFolder != null ? this.currentFolder.name : '';
            suggestedName = selectedForm.proposeAutoNumberName(numTemplateFormInstancesInFolder, folderName);
        } else {
            // Use the original strategy for proposing a new form instance name.
            //
            // SWH note:  could someone with more knowledge about the following logic that I have write a meaningful comment on what this logic is doing?
            let rx = new RegExp(`${selectedForm.name} \\(\\d+\\)$`);
            let nameCount = this.existingFormInstances.filter(x => { return x.name == selectedForm.name; }).length;
            nameCount = nameCount + this.existingFormInstances.filter(x => { return x.name.match(rx) }).length;

            suggestedName = selectedForm.name;
            if (nameCount > 0) {
                suggestedName = `${suggestedName} (${nameCount})`;
            }
        }

        return suggestedName;
    }
}
